import {
  ACTIVE_STRATEGY_KEYS,
  type Channel,
  type StrategyKey,
  strategyKeysFor,
} from '@sbc/domain-models/gateway';

// One source of truth for how each engagement strategy is named and explained in the admin UI —
// shared by the binding form's picker help and the connection page's routing explainer so the two
// never drift. `applies` describes the inbound event that triggers the strategy (see how the
// gateway routes a comment by the post owner's account: the owner tagged → fan out, another
// connected account tagged → that account's reply to mention).
// Which channel can run which strategy is not here: it is a routing rule the server enforces too,
// so it lives in `STRATEGY_CHANNELS` in the domain model and is read through `strategyKeysFor`.
export interface StrategyMeta {
  label: string;
  help: string;
  applies: string;
}

export const STRATEGIES: Record<StrategyKey, StrategyMeta> = {
  'reply-to-dm': {
    label: 'Reply to DM',
    help: 'Handles direct messages to the account and replies in the DM thread.',
    applies: 'Someone sends the account a direct message.',
  },
  'fan-out-on-own-media-comment': {
    label: 'Fan out on comment',
    help: 'Answers an @mention in a comment on our own post with a public reply, then bridges it into a one-time private DM.',
    applies: "Someone @mentions the account in a comment on the account's own post.",
  },
  'reply-to-mention': {
    label: 'Reply to mention',
    help: "Answers an @mention in a comment on another connected account's post with a single public in-thread reply, posted as this account (no DM — Meta allows private replies only from the post's owner).",
    applies: "Someone tags this account in a comment on another connected account's post.",
  },
  'investigate-on-flag': {
    label: 'Investigate on flag (retired)',
    help: 'Retired in ENG-1822. The investigator now runs as a sub-run inside a Draft CS reply turn, so nothing dispatches this strategy any more.',
    applies: 'Nothing. The SBC relay that fed it is gone.',
  },
  'draft-on-inbound': {
    label: 'Draft on inbound',
    help: 'Drafts a reply for every new inbox message and pre-fills it in the review box. Nothing is sent until a person clicks Send.',
    applies: 'Someone comments on a post or sends a DM and the conversation needs a reply.',
  },
  'draft-cs-reply': {
    label: 'Draft CS reply',
    help: 'Drafts a support reply for every customer message on the thread and posts it as an internal note. Nothing reaches the customer until a CS agent sends it themselves.',
    applies: 'A customer starts or replies to a support conversation.',
  },
  'run-on-request': {
    label: 'Run on request',
    help: 'Runs this agent once against the payload SBC sends, then posts the structured result back to SBC. Nothing here reads or writes a customer-facing channel.',
    applies: 'The SBC app posts a job naming this agent.',
  },
  'reply-when-addressed': {
    label: 'Reply when addressed',
    help: 'Answers in the thread when someone @mentions the agent in a channel it is in, DMs it, or replies in a thread it is already part of. The agent may also choose to stay silent.',
    applies: 'Someone mentions or DMs the Slack app, or follows up in its thread.',
  },
  'reply-to-every-message': {
    label: 'Reply to every message',
    help: "Answers every person's message in one Slack channel with a reply in that message's thread, mentions included. Edits that change the text come back as a new turn. The agent may also choose to stay silent.",
    applies: 'Someone posts in the watched Slack channel.',
  },
  'append-to-every-message': {
    label: 'Append to every message',
    help: "Writes the agent's answer under the author's own message in one Slack channel, once that person has connected their Slack account through the link the app offers. Until then, or when Slack refuses the edit, it replies in the message's thread. Edits that change the text come back as a new turn. The agent may also choose to stay silent.",
    applies: 'Someone posts in the watched Slack channel.',
  },
  'reply-to-shortcut': {
    label: 'Reply to shortcut',
    help: "Answers privately to whoever clicks the Slack app's message shortcut, on any message they can see. Each click is its own single-turn session. When the agent records nothing, the clicker sees a fixed line saying the message could not be translated.",
    applies: "Someone runs the app's shortcut on a message.",
  },
};

// Offered by the binding picker before a connection narrows it. A retired strategy declares no
// channel, so it never appears here, and the write contract rejects it server-side too.
export const SELECTABLE_STRATEGY_KEYS: StrategyKey[] = [...ACTIVE_STRATEGY_KEYS];

// Which strategy a binding form is really on, given the channel it is scoped to and the key
// it happens to hold. The picker offers only what the channel can run, so a held key can sit
// outside that list — and a `<select>` whose value matches no option still displays the first
// one, so the form looked right while submitting the stale key. Resolving it here, rather than
// patching state inside a change handler, is what keeps submitted equal to displayed: a
// connection preselected from `?connectionId=` never fires a change handler at all.
export function resolveStrategyKey(channel: Channel | undefined, held: StrategyKey): StrategyKey {
  const allowed = channel ? strategyKeysFor(channel) : SELECTABLE_STRATEGY_KEYS;
  return allowed.includes(held) ? held : (allowed[0] ?? held);
}
